import {Box, Button, IconButton, Stack, Tooltip, Typography} from "@mui/material";
import Modal from "@mui/material/Modal";
import {useAtom} from "jotai";
import {SelectedConversationAtom} from "../atoms";
import {getConversationIcon} from "../../../../utils/functions";
import moment from "moment";
import {IconBug, IconChevronLeft, IconChevronRight, IconEye, IconMessage2} from "@tabler/icons-react";
import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {ConversationRepository} from "../../../../repositories/ConversationRepository";
import {toast} from "react-toastify";
import {SignInContext} from "../../../../context/SignInContext";
import useConfirm from "../../../../hooks/useConfirm";
import {SupportAgent} from "@mui/icons-material";
import {SupervisionHistoryItem} from "../../supervision-page/components/supervision-history-item";
import {ContactPhoneNumber} from "../../supervision-page/components/contact-phone-number";
import {useStompWebsocket} from "../../../../hooks/useStompWebsocket";

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: "xl", width: "90%",
  maxHeight: "80vh", height: "100%",
  bgcolor: 'background.paper',
  borderRadius: 2,
  border: '0px solid #000',
  boxShadow: 24,
  p: 3,
  overflowY: "auto"
};

export const ConversationPreviewModal = ({project}) => {
  const [selectedConversation, setSelectedConversation] = useAtom(SelectedConversationAtom);
  // const setOpenComment = useSetAtom(CommentConversationAtom);
  const open = !!selectedConversation;
  const [prev, setPrev] = useState(null);
  const [next, setNext] = useState(null);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState(null);
  const {currentUser} = useContext(SignInContext);
  const [showDebug, setShowDebug] = useState(false);
  const [messages, setMessages] = useState([]);
  const messagesAbortController = useRef(null);

  const [updatedValue] = useStompWebsocket(
    selectedConversation?.status === "ACTIVE" ? `/topic/session/${selectedConversation?.id}`: null
  );

  const confirmMonitoring = useConfirm(
    "This conversation is already being monitored by " + selectedConversation?.monitoredBy || "someone",
    "Are you sure you want to take over monitoring from " + selectedConversation?.monitoredBy || "someone" + "?"
  );

  useEffect(() => {
    if (selectedConversation?.id) {
      fetchMessages();
    }
  }, [updatedValue, selectedConversation?.id]);

  useEffect(() => {
    const debug = localStorage.getItem("showDebug");
    setShowDebug(debug ? JSON.parse(debug) || false : false);
  }, [selectedConversation?.id]);

  // Scroll to message when messages are loaded and messageId is provided
  useEffect(() => {
    if (selectedConversation?.messageId && messages.length > 0) {
      // Use setTimeout to ensure the DOM is fully rendered
      setTimeout(() => {
        const messageElement = document.getElementById(`message-${selectedConversation?.messageId}`);
        if (messageElement) {
          messageElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 100);
    }
  }, [selectedConversation?.messageId, messages]);

  useEffect(() => {
    if (selectedConversation?.id) {
      ConversationRepository.getPrevAndNext({id: selectedConversation?.id, flow: selectedConversation?.flowUid})
        .then(response => {
          setPrev(response?.data?.prev || null);
          setNext(response?.data?.next || null);
        })
        .catch(() => {
          setPrev(null);
          setNext(null);
        })
    } else {
      setPrev(null);
      setNext(null);
    }
  }, [selectedConversation?.id]);

  const fetchMessages = () => {
    // Abort previous request if it exists
    if (messagesAbortController.current) {
      messagesAbortController.current.abort();
    }

    const controller = new AbortController();
    messagesAbortController.current = controller;
    ConversationRepository.getMessages(selectedConversation.id, controller.signal)
      .then(response => setMessages(response?.data))
      .catch(error => console.log(error))


  }

  const handleClose = () => {
    setSelectedConversation(null);
  }

  const handleHandover = () => {
    console.log("Handover", currentUser?.username);
    const vdnManager = project?.vdnManager || {};
    let vdn = project?.configuration?.redirect_vdn;
    if (vdnManager[currentUser?.username]) {
      vdn = vdnManager[currentUser?.username];
    }

    ConversationRepository.handoverToAgent({id: selectedConversation?.id, username: currentUser?.username, vdn: vdn})
      .then(response => {
        setSelectedConversation(prev => response?.data || prev);
        toast.success("Successfully handover to agent")
      })
      .catch(() => {
        toast.error("Failed to handover to agent")
      })
  }
  const monitorConversation = async () => {
    if (selectedConversation?.monitoredBy && selectedConversation?.monitoredBy !== currentUser?.username) {
      const confirmed = await confirmMonitoring();

      if (!confirmed) return;
    }
    ConversationRepository.monitorConversation({id: selectedConversation?.id, username: currentUser?.username})
      .then(response => {
        setSelectedConversation(prev => response?.data || prev);
        toast.success("You are now monitoring conversation")
      })
      .catch(() => {
        toast.error("Failed to monitor conversation")
      })
  }
  
  const contact = useMemo(() => {
    return project?.phonebook?.[selectedConversation?.sender] || null;
  }, [project?.phonebook?.[selectedConversation?.sender], selectedConversation?.sender])
  const displayName = contact || selectedConversation?.sender;
  const truncatedName = displayName?.length > 20 ? `${displayName.slice(0, 20)}...` : displayName;
  return (
    <>
      <ContactPhoneNumber
        project={project} phoneNumber={selectedPhoneNumber}
        open={selectedPhoneNumber !== null}
        handleClose={() => setSelectedPhoneNumber(null)}
      />
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Stack direction={"column"} gap={0} sx={style}>
          <Stack direction={"row"} gap={2} justifyContent={"space-between"}>
            <Stack direction={"row"} gap={2} alignItems={"center"}>
              {getConversationIcon(selectedConversation?.status)}
              <Box>
                <Tooltip title={`${selectedConversation?.sender} - ${contact || "Add as contact"}`}>
                  <Typography sx={{cursor: "pointer"}} fontWeight={500} onClick={() => setSelectedPhoneNumber(selectedConversation?.sender)}>
                    {selectedConversation?.id}. {truncatedName}
                  </Typography>
                </Tooltip>
                <Typography fontSize={9}>
                  {moment(selectedConversation?.dateCreated)?.format("DD MMM YYYY HH:mm")}
                </Typography>
              </Box>
            </Stack>
            <Box>
              {
                selectedConversation?.monitoredBy ?
                  <Typography fontSize={13}>
                    This conversation is currently being monitored by <span><strong style={{fontWeight: 600}}>{selectedConversation.monitoredBy}</strong></span>.

                  </Typography>
                  :
                  <Typography fontSize={13}>
                    No one is currently supervising this conversation. Do you want to start supervising?
                  </Typography>
              }

            </Box>

            <Stack direction={"row"} gap={1} justifyContent={"flex-start"} alignItems={"center"}>
              <Box>
                <IconButton disabled={prev === null}
                            onClick={() => setSelectedConversation(prev)}><IconChevronLeft/></IconButton>
              </Box>
              <Box>
                <IconButton disabled={next === null}
                            onClick={() => setSelectedConversation(next)}><IconChevronRight/></IconButton>
              </Box>
              <Box>
                <Tooltip title={"Show Debug Messages"}>
                  <IconButton
                    size={"small"}
                    color={showDebug ? "success" : "default"}
                    onClick={() => {
                      setShowDebug(prev => {
                        localStorage.setItem("showDebug", JSON.stringify(!prev))
                        return !prev;
                      })
                    }}
                  >
                    <IconBug size={22}/>
                  </IconButton>
                </Tooltip>
              </Box>
              <Box>
                <Button
                  variant={"outlined"} color={"info"} size={"small"}
                  sx={{borderRadius: 2}}
                  startIcon={<IconEye size={18}/>}
                  tooltip={"Supervision"}
                  onClick={monitorConversation}
                >
                  Supervise
                </Button>
              </Box>
              <Box>
                <Button
                  disabled={selectedConversation?.status !== "ACTIVE"}
                  size={"small"}
                  startIcon={<SupportAgent size={18}/>}
                  onClick={handleHandover}
                  color={"info"} variant={"contained"} sx={{borderRadius: 2, boxShadow: 0}}
                >
                  Handover
                </Button>
              </Box>
            </Stack>
          </Stack>
          <Stack direction={"column"} gap={1} sx={{flex: 1, overflowY: "auto"}}>
            <SupervisionHistoryItem
              conversation={selectedConversation}
              messages={messages?.sort((a, b) => a.id - b.id)}
              project={project}
              loadDuration={true}
              showUserTalking={true}
              showDebug={showDebug}
              updateConversation={(conversation) => {
                setSelectedConversation(prev => ({...prev, ...conversation}));
              }}
              fetchMessages={fetchMessages}
            />
            {/*<SupervisionConversationHistory selectedConversation={selectedConversation}*/}
            {/*                                setSelectedConversation={setSelectedConversation}*/}
            {/*                                debug={showDebug} project={project} />*/}
          </Stack>
        </Stack>
      </Modal>
    </>
  )
}